import { IdSchema } from "@hisani/core";
import { z } from "zod";
export const STABLE_ERROR_CODES = {
    notFound: "STABLE_NOT_FOUND",
    forbidden: "STABLE_FORBIDDEN",
    invalidRequest: "STABLE_INVALID_REQUEST",
    conflict: "STABLE_CONFLICT",
    invalidTransition: "STABLE_INVALID_TRANSITION",
    capacityLimit: "STABLE_CAPACITY_LIMIT",
    rateLimited: "STABLE_RATE_LIMITED",
    unavailable: "STABLE_UNAVAILABLE",
};
export { ENTITY_ROLES, STABLE_FEATURES, EntityRoleSchema, StableFeatureSchema, FeatureAccessLevelSchema, } from "./domain/membership/entity-roles.js";
export { EXTRACTION_KINDS, EXTRACTION_PHOTO_CONTENT_TYPES, EXTRACTION_PHOTO_FILENAME_MAX_LENGTH, EXTRACTION_PHOTO_MAX_SIZE_BYTES, ExtractionKindSchema, matchesExtractionPhotoExtension, } from "./domain/extraction/extraction-kind.js";
export { HORSE_SCAN_COMPULSORY_FIELD_NAMES, HORSE_SCAN_FIELD_NAMES, HORSE_SCAN_KINDS, buildHorseScanFields, horseFieldsToScanSnapshot, isHorseScanKind, missingCompulsoryHorseScanFields, } from "./domain/extraction/horse-scan-extraction.js";
export { VACCINATION_SCAN_COMPULSORY_FIELD_NAMES, VACCINATION_SCAN_FIELD_NAMES, VACCINATION_SCAN_KINDS, buildVaccinationScanFields, isVaccinationScanKind, missingCompulsoryVaccinationScanFields, } from "./domain/extraction/vaccination-scan-extraction.js";
export { EXTRACTION_PDF_CONTENT_TYPES, EXTRACTION_PDF_MAX_SIZE_BYTES, EXTRACTION_PDF_ELIGIBLE_KINDS, acceptsExtractionPdf, isExtractionPdfContentType, matchesExtractionPdfExtension, } from "./domain/extraction/extraction-kind.js";
export { BILL_SCAN_COMPULSORY_FIELD_NAMES, BILL_SCAN_FIELD_NAMES, BILL_SCAN_KINDS, buildBillScanFields, isBillScanKind, missingCompulsoryBillScanFields, } from "./domain/extraction/bill-scan-extraction.js";
export { ExpenseCategoryCodeSchema, ExpenseCategoryConfigSchema, isKnownExpenseCategory, } from "./domain/accounting/expense-category.js";
export { AmountMinorSchema, CurrencyCodeSchema, currencyMinorUnitDigits, majorAmountToMinor, minorAmountToMajor, sumAmountsMinor, } from "./domain/accounting/money.js";
export { BASIS_POINTS_WHOLE, BasisPointsSchema, basisPointsSumToWhole, evenBasisPointSplit, splitAmountByBasisPoints, } from "./domain/accounting/percentage-allocation.js";
export { BILL_ALLOCATION_MODES, BillAllocationError, BillAllocationInputSchema, BillAllocationModeSchema, allocateBillAmount, resolveBillAllocation, } from "./domain/accounting/bill-allocation.js";
export { InvoiceSnapshotError, buildInvoiceSnapshot, } from "./domain/accounting/invoice-snapshot.js";
export { NO_PRIMARY_OWNER_REASON } from "./domain/accounting/owner-link.js";
export { VaccineTypeCodeSchema, VaccineTypeConfigSchema, isKnownVaccineType, } from "./domain/vaccinations/vaccine-type.js";
export { computeNextDue } from "./domain/vaccinations/vaccination-interval.js";
export { VACCINATION_ALERT_EVENT_TYPES, VACCINATION_ALERT_SEVERITIES, VaccinationAlertSeveritySchema, decideVaccinationAlert, isAcknowledgedForCycle, vaccinationAlertSeverity, } from "./domain/vaccinations/vaccination-alert.js";
export { HORSE_BREEDS, HORSE_SEXES, HORSE_COLORS, HorseBreedSchema, HorseSexSchema, HorseColorSchema, } from "./domain/horses/horse-details.js";
export { HORSE_LIFECYCLE_STATUSES, HORSE_RECORD_STATES, HORSE_INACTIVE_REASONS, } from "./domain/horses/horse-lifecycle.js";
export { MASS_UNITS, TEMPERATURE_UNITS, MassUnitSchema, TemperatureUnitSchema, } from "./domain/care-records/care-record.js";
export { DISTANCE_UNITS, DistanceUnitSchema, } from "./domain/training/training-entry.js";
export const StableSubscriptionTierSchema = z.enum(["FREE", "PAID"]);
/** Validates the scalar `stable_policies` row only — `vaccineTypes` is
 * composed separately by the repository from `stable_vaccine_types` (a
 * proper table, not a JSONB blob, per `packages/database/AGENTS.md`: "Do not
 * replace normal modeling with generic JSONB blobs" — vaccine types are
 * variable-shaped configuration data, not a fixed set of scalar levers). */
export const StablePolicySchema = z
    .object({
    freeTierActiveHorseCap: z.number().int().min(1).max(1000),
    staffInviteExpiryDays: z.number().int().min(1).max(365),
    publicIdLookupRateLimitPerHour: z.number().int().min(1).max(1000),
    vaccinationAlertWarningLeadDays: z.number().int().min(1).max(180),
    vaccinationAlertNudgeLeadDays: z.number().int().min(1).max(180),
    ownerLinkRequestExpiryDays: z.number().int().min(1).max(365),
    stableTransferGracePeriodDays: z.number().int().min(1).max(365),
    stableTransferRequestExpiryDays: z.number().int().min(1).max(365),
})
    .strict()
    .refine((value) => value.vaccinationAlertNudgeLeadDays <
    value.vaccinationAlertWarningLeadDays, {
    message: "vaccinationAlertNudgeLeadDays must be less than vaccinationAlertWarningLeadDays.",
    path: ["vaccinationAlertNudgeLeadDays"],
});
/** Working defaults per `docs/products/stable.md` "Open questions": ~6-month
 * (183-day) equine-influenza interval with alerting on; other types
 * recordable now with alerting off, per COMPANY.md Future Scope #12. */
const DEFAULT_VACCINE_TYPES = {
    EQUINE_INFLUENZA: {
        code: "EQUINE_INFLUENZA",
        label: "Equine Influenza",
        intervalDays: 183,
        alertingEnabled: true,
    },
    TETANUS: {
        code: "TETANUS",
        label: "Tetanus",
        intervalDays: 365,
        alertingEnabled: false,
    },
    EQUINE_HERPESVIRUS: {
        code: "EQUINE_HERPESVIRUS",
        label: "Equine Herpesvirus (EHV)",
        intervalDays: 180,
        alertingEnabled: false,
    },
};
/** Working defaults per `docs/products/stable.md` "Accounting": "Feed,
 * Stable Supplies, Horse Care Supplies, etc." — illustrative, not
 * exhaustive; a Stable/Admin can add more as configuration (see
 * `expense-category.ts`'s own doc comment). */
const DEFAULT_EXPENSE_CATEGORIES = {
    FEED: { code: "FEED", label: "Feed" },
    STABLE_SUPPLIES: { code: "STABLE_SUPPLIES", label: "Stable Supplies" },
    HORSE_CARE_SUPPLIES: {
        code: "HORSE_CARE_SUPPLIES",
        label: "Horse Care Supplies",
    },
    VETERINARY: { code: "VETERINARY", label: "Veterinary" },
    FARRIER: { code: "FARRIER", label: "Farrier" },
    TRANSPORT: { code: "TRANSPORT", label: "Transport" },
    OTHER: { code: "OTHER", label: "Other" },
};
export const DEFAULT_STABLE_POLICY = {
    ...StablePolicySchema.parse({
        freeTierActiveHorseCap: 5,
        staffInviteExpiryDays: 30,
        publicIdLookupRateLimitPerHour: 20,
        vaccinationAlertWarningLeadDays: 30,
        vaccinationAlertNudgeLeadDays: 7,
        ownerLinkRequestExpiryDays: 30,
        stableTransferGracePeriodDays: 30,
        stableTransferRequestExpiryDays: 30,
    }),
    vaccineTypes: DEFAULT_VACCINE_TYPES,
    expenseCategories: DEFAULT_EXPENSE_CATEGORIES,
};
export const OWNER_LINK_REQUEST_STATUSES = [
    "PENDING",
    "ACCEPTED",
    "DECLINED",
    "CANCELLED",
    "EXPIRED",
];
export const OwnerLinkRequestStatusSchema = z.enum(OWNER_LINK_REQUEST_STATUSES);
export const StableOutboxEventTypeSchema = z.enum([
    "STABLE_INVITE_SENT",
    "STABLE_INVITE_ACCEPTED",
    "STABLE_STAFF_ADDED",
    "STABLE_STAFF_REMOVED",
    "STABLE_ENTITY_OWNER_TRANSFER_REQUESTED",
    "STABLE_ENTITY_OWNER_TRANSFER_ACCEPTED",
    "STABLE_ENTITY_OWNER_TRANSFER_DECLINED",
    "VACCINATION_DUE_SOON",
    "VACCINATION_NUDGE",
    "VACCINATION_OVERDUE",
    "ACCOUNTING_INVOICE_SHARED",
    "STABLE_OWNER_LINK_REQUESTED",
    "STABLE_OWNER_LINK_ACCEPTED",
    "STABLE_OWNER_LINK_DECLINED",
    "HIRE_APPLICATION_SUBMITTED",
    "HIRE_MESSAGE_RECEIVED",
    "STABLE_TRANSFER_REQUESTED",
    "STABLE_TRANSFER_ACCEPTED",
    "STABLE_TRANSFER_DECLINED",
    "STABLE_TRANSFER_HORSE_DELETED",
]);
export const StableOutboxEventSchema = z
    .object({
    id: IdSchema,
    type: StableOutboxEventTypeSchema,
    occurredAt: z.date(),
})
    .strict();
// Not exported: unlike `BILL_ALLOCATION_MODES`/`BILL_SCAN_KINDS`, nothing
// outside this schema needs the raw literal tuple, only the derived
// `InvoiceStatusSchema`/`InvoiceStatus` below.
const INVOICE_STATUSES = ["DRAFT", "APPROVED"];
export const InvoiceStatusSchema = z.enum(INVOICE_STATUSES);
